/**
 * What the policy pages print about this storefront, derived from the resolved config: the
 * contracting party, where to write, the site, and the currency's name. Pure, so the wording
 * rules are testable without a page.
 */
import type { Currency, PublicStorefrontLegal } from "@loot-os/api-contract";

import type { TenantConfig } from "./tenant.js";

/** When the text of each document last changed; a code constant, not a per-store value. */
export const TERMS_LAST_UPDATED = "September 17, 2026";
export const PRIVACY_LAST_UPDATED = "September 18, 2026";
/** The storefront root's own Privacy Policy (Loot Labs' for Loot OS, `PlatformPrivacyPage`). */
export const PLATFORM_PRIVACY_LAST_UPDATED = "September 30, 2026";

/** Everything the two documents interpolate. */
export interface LegalParties {
  /** The contracting entity with its trade name: "Loot Labs, Inc. dba Acme Cards". */
  company: string;
  /** The entity alone, for the consumer notice and the copyright line. */
  legalName: string;
  /** The store as shoppers know it. */
  brand: string;
  /** The store's own origin, "" on a stage with no hostname. */
  siteUrl: string;
  supportEmail: string;
  legalEmail: string;
  postalAddress: string;
  governingLaw: string;
  arbitrationVenue: string;
  /** The currency's unit names, capitalized for a defined term: "Credit" / "Credits". */
  unit: string;
  units: string;
  /** The year on the copyright line. */
  year: number;
}

function capitalizeFirst(word: string): string {
  return word ? word.charAt(0).toUpperCase() + word.slice(1) : word;
}

/**
 * The entity as the terms open with it: the legal name, plus "dba {brand}" when the brand
 * is not already part of it ("Acme Cards, Inc." trading as "Acme Cards" needs no suffix).
 */
export function companyDisplayName(legalName: string, brand: string): string {
  const name = legalName.trim();
  const trade = brand.trim();
  if (!trade || name.toLowerCase().includes(trade.toLowerCase())) return name;
  return `${name} dba ${trade}`;
}

/** The currency's unit as a defined term: capitalized singular and plural. */
export function currencyTerms(currency: Pick<Currency, "name" | "namePlural">): {
  unit: string;
  units: string;
} {
  return { unit: capitalizeFirst(currency.name), units: capitalizeFirst(currency.namePlural) };
}

/** The store's origin from its primary domain; "" when the stage gave it none. */
export function storefrontSiteUrl(primaryDomain: string): string {
  return primaryDomain ? `https://${primaryDomain}` : "";
}

export function legalParties(
  config: Pick<TenantConfig, "name" | "primaryDomain" | "currency"> & {
    legal: PublicStorefrontLegal;
  },
  now: Date = new Date(),
): LegalParties {
  const { unit, units } = currencyTerms(config.currency);
  return {
    company: companyDisplayName(config.legal.legalName, config.name),
    legalName: config.legal.legalName,
    brand: config.name,
    siteUrl: storefrontSiteUrl(config.primaryDomain),
    supportEmail: config.legal.supportEmail,
    legalEmail: config.legal.legalEmail,
    postalAddress: config.legal.postalAddress,
    governingLaw: config.legal.governingLaw,
    arbitrationVenue: config.legal.arbitrationVenue,
    unit,
    units,
    year: now.getUTCFullYear(),
  };
}
